import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Full MINDESIGN ecosystem — grouped for a compact desktop nav.
  const menuGroups = [
    { type: 'link', name: 'About', href: '#about' },
    {
      type: 'group',
      name: 'Stories',
      children: [
        { name: 'Literary Works', href: '#books' },
        { name: 'Comics', href: '#comics' },
        { name: 'NFT Gallery', href: '#nfts' },
        { name: "Founder's Story", href: '#founders-story' },
      ],
    },
    {
      type: 'group',
      name: 'Sound',
      children: [
        { name: 'Music', href: '#music' },
        { name: 'MINDESIGN Radio', href: '#radio' },
        { name: 'The Amped Up Life · Podcast', href: '#amped-up-life' },
        { name: 'License Music', href: '#music-licensing' },
      ],
    },
    {
      type: 'group',
      name: 'Philosophy',
      children: [
        { name: 'Four Pillars', href: '#pillars' },
        { name: 'Four More', href: '#four-more' },
        { name: 'Blog', href: '#blog' },
      ],
    },
    {
      type: 'group',
      name: 'Commerce',
      children: [
        { name: 'Shop', href: '#shop' },
        { name: 'Give More', href: '#give-more' },
        { name: 'Proud Partners', href: '#partners' },
      ],
    },
    { type: 'link', name: 'After Dark · 18+', href: '#afterdark', accent: true },
    { type: 'link', name: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
      setOpenDropdown(null);
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-gunmetal-900/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" className="flex items-center space-x-2 group" data-testid="nav-logo">
            <div className="text-2xl font-bold tracking-tight">
              <span className="text-steel-200 group-hover:text-accent-red transition-colors duration-300">
                MINDESIGN
              </span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div
            className="hidden lg:flex items-center space-x-6"
            ref={dropdownRef}
            data-testid="nav-desktop"
          >
            {menuGroups.map((item) => {
              if (item.type === 'link') {
                const accentClass = item.accent
                  ? 'text-pink-400 hover:text-pink-300 font-bold'
                  : 'text-steel-300 hover:text-accent-red';
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => scrollToSection(e, item.href)}
                    className={`${accentClass} transition-colors duration-300 text-xs font-medium tracking-[0.15em] uppercase`}
                    data-testid={`nav-link-${item.name}`}
                  >
                    {item.name}
                  </a>
                );
              }
              const isOpen = openDropdown === item.name;
              return (
                <div key={item.name} className="relative">
                  <button
                    onClick={() => setOpenDropdown(isOpen ? null : item.name)}
                    className="flex items-center gap-1 text-steel-300 hover:text-accent-red transition-colors duration-300 text-xs font-medium tracking-[0.15em] uppercase"
                    data-testid={`nav-dropdown-trigger-${item.name}`}
                  >
                    {item.name}
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {isOpen && (
                    <div
                      className="absolute left-0 top-full mt-3 w-56 bg-gunmetal-900/98 backdrop-blur-md border border-steel-700/40 rounded-md shadow-2xl shadow-black/60 py-2"
                      data-testid={`nav-dropdown-${item.name}`}
                    >
                      {item.children.map((child) => (
                        <a
                          key={child.name}
                          href={child.href}
                          onClick={(e) => scrollToSection(e, child.href)}
                          className="block px-4 py-2 text-sm text-steel-300 hover:text-accent-red hover:bg-gunmetal-800 transition-colors"
                          data-testid={`nav-subitem-${child.name}`}
                        >
                          {child.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <Button
              className="bg-accent-red hover:bg-accent-red-dark text-white font-semibold px-5 py-2 text-xs tracking-[0.15em] uppercase transition-all duration-300 hover:shadow-lg hover:shadow-accent-red/30"
              onClick={(e) => scrollToSection(e, '#contact')}
              data-testid="nav-cta-btn"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-steel-200 hover:text-accent-red transition-colors duration-300"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="nav-mobile-toggle"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden bg-gunmetal-900/98 backdrop-blur-md border-t border-steel-700/30 max-h-[80vh] overflow-y-auto"
          data-testid="nav-mobile-menu"
        >
          <div className="px-4 py-6 space-y-5">
            {menuGroups.map((item) => {
              if (item.type === 'link') {
                const mobileAccent = item.accent
                  ? 'text-pink-400 hover:text-pink-300 font-bold'
                  : 'text-steel-200 hover:text-accent-red';
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e) => scrollToSection(e, item.href)}
                    className={`block ${mobileAccent} transition-colors duration-300 text-sm font-medium tracking-[0.15em] uppercase`}
                  >
                    {item.name}
                  </a>
                );
              }
              return (
                <div key={item.name} className="space-y-2">
                  <div className="text-[10px] text-amber-400/80 tracking-[0.25em] uppercase">
                    {item.name}
                  </div>
                  {item.children.map((child) => (
                    <a
                      key={child.name}
                      href={child.href}
                      onClick={(e) => scrollToSection(e, child.href)}
                      className="block text-steel-300 hover:text-accent-red transition-colors duration-300 text-sm tracking-wide pl-3 border-l border-steel-700/60"
                    >
                      {child.name}
                    </a>
                  ))}
                </div>
              );
            })}
            <Button
              className="w-full bg-accent-red hover:bg-accent-red-dark text-white font-semibold py-3 mt-4"
              onClick={(e) => scrollToSection(e, '#contact')}
            >
              Get Started
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};
